import { Box, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { FlexColBox, Icon } from "../../../../common/components";
import { MaterialSymbolType } from "../../../../common/components/Icon/IconConstant";
import { useSelector } from "../../../../common/hooks/useSelector";
import { useStudio } from "../../../../context/Studio/useStudio";
import { palette, spacing } from "../../../../styles/theme";
import {
  DecorationImage,
  DesignDecoration,
  GLASS_MAGNIFIER_BACKGROUND_COLORS,
  ProductDefinitionPartElementDisplay,
} from "../../../../types";
import { DESIGN_CHANGE_ENUM } from "../../../../utils/ReactHooks";
import { useThemeBreakpoints } from "../../../../utils/themeUtils";
import { LeftSidebarItem } from "../components";
import ImageService from "../../../../service/ImageService";
import { getFileExtensionFromFileName } from "../../../../utils/file.utils";
import { NON_SUPPORTED_BROWSER_FILE_EXTENSIONS } from "../../../../constant/common.constant";

const Decorations = () => {
  const { isMobile } = useThemeBreakpoints();
  const { t, i18n } = useTranslation();
  const [resolvedDecorationImages, setResolvedDecorationImages] = useState<
    { assetId: string; image: DecorationImage }[]
  >([]);

  const {
    canUpdateArtworkPosition,
    canAddArtwork,
    canDeleteArtwork,
    ...userInfo
  } = useSelector((state) => state.userInfo);
  const isAdmin = userInfo.isAdmin || userInfo.isDesigner;
  const {
    designState: {
      designDecorationsFromDesignState: decorations = [],
      onSelectionChange,
      onDesignChange,
      selection,
      isUpdating,
    },
    teamId,
    isDesignLocked,
  } = useStudio();

  const addDecoration = async () => {
    const decorationId = uuidv4();
    await onDesignChange(DESIGN_CHANGE_ENUM.ADD_DECORATION, {
      group: "print",
      decorationId: decorationId,
      name: "default",
    });

    onSelectionChange("Decoration", decorationId);
  };

  const getDecorationName = (decoration: DesignDecoration) => {
    if (decoration.name === "default") {
      return t("studio.decorations.select_image");
    }

    return i18n.exists(`specs.decoration_types.${decoration.name}.title`)
      ? t(`specs.decoration_types.${decoration.name}.title`)
      : decoration.name;
  };

  useEffect(() => {
    const fetchDecorationImages = async () => {
      const imagePromises = decorations
        .filter((d) => d.assetId)
        .map((d) => {
          return {
            assetId: d.assetId,
            image: isAdmin
              ? (ImageService.getAssetAsAdmin(
                  d.assetId,
                  d.assetVersionId,
                  d.type,
                  null
                ) as Promise<DecorationImage>)
              : (ImageService.getAsset(
                  d.assetId,
                  d.assetVersionId,
                  teamId,
                  d.type,
                  null
                ) as Promise<DecorationImage>),
          };
        });

      const images = await Promise.all(
        imagePromises.map(async (promise) => {
          const image = await promise.image;
          return { ...promise, image };
        })
      );
      setResolvedDecorationImages(images);
    };

    fetchDecorationImages();
  }, [decorations, isAdmin, teamId]);

  const getDecorationImageUrl = (
    decorationImage: DecorationImage | undefined
  ) => {
    const fileExtension = getFileExtensionFromFileName(
      decorationImage?.assetName ?? ""
    );

    const isSupportedFileExtension =
      !NON_SUPPORTED_BROWSER_FILE_EXTENSIONS.includes(fileExtension ?? "");

    if (decorationImage?.originalUrl && isSupportedFileExtension) {
      return decorationImage.originalUrl;
    }
    return decorationImage?.fullSizeUrl;
  };

  const options = decorations?.map((decoration) => {
    const decorationImage = resolvedDecorationImages?.find(
      (r) => r.assetId === decoration.assetId
    )?.image;
    const name = getDecorationName(decoration);
    const menuIconUrl =
      getDecorationImageUrl(decorationImage) || decoration.icon || "";
    return {
      label: name,
      deprecated: decoration.deprecated,
      display: {
        menuIconUrl: menuIconUrl,
        icon: "upload" as MaterialSymbolType,
        displayable: true,
        displayMethod: "icon",
        backgroundColor: decoration.previewBackgroundColor
          ? decoration.previewBackgroundColor
          : decorationImage
            ? GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT
            : undefined,
      } as ProductDefinitionPartElementDisplay,
      onClick: () => {
        if (isMobile) {
          selection.selectedElementName === decoration.id
            ? onSelectionChange("", "")
            : onSelectionChange("Decoration", decoration.id);
        } else {
          onSelectionChange("Decoration", decoration.id);
        }
      },
      onHide: () => {
        onDesignChange(DESIGN_CHANGE_ENUM.SET_DECORATION_ACTIVE, {
          decorationId: decoration.id,
          active: false,
        });
      },
      onShow: () => {
        onDesignChange(DESIGN_CHANGE_ENUM.SET_DECORATION_ACTIVE, {
          decorationId: decoration.id,
          active: true,
        });
      },
      onDelete: () => {
        onSelectionChange("", "", "");
        onDesignChange(DESIGN_CHANGE_ENUM.ON_REMOVE, {
          decorationId: decoration.id,
        });
      },
      isSelected:
        selection.selectedPart === "Decoration" &&
        selection.selectedElementName === decoration.id,
      canDelete: canDeleteArtwork,
      canHide: !!decoration.assetId && canUpdateArtworkPosition,
      active: decoration.active && !decoration.inError,
      enabled: !decoration.deprecated,
      id: decoration.id,
      isDefault: decoration.name === "default",
      // inWarning is true when the decoration is in warning or error || if a color is disabled in the decoration
      inWarning: decorationImage?.selectedColors?.some(
        (c) => c.chartColor.disabled
      ),
    };
  });

  return (
    <FlexColBox
      gap={{ xs: spacing.wrapperMobile, lg: spacing.smallDesktop }}
      pr={{ xs: "12px", lg: 0 }} //spacing at the end of the row
      flexDirection={{ xs: "row", lg: "column" }}
    >
      {!isMobile && (
        <FlexColBox width={"100%"}>
          <Typography
            mb={{ xs: spacing.mediumMobile, lg: spacing.mediumDesktop }}
            variant="textRegularBold"
          >
            {t(`specs.decorations`)}
          </Typography>

          <Box
            onClick={isDesignLocked || isUpdating ? undefined : addDecoration}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            p={"8px 8px 8px 12px"}
            borderRadius={"8px"}
            border={`1px solid ${palette.grey.darkGrey}`}
            sx={{
              cursor: "pointer",
              userSelect: "none",
              pointerEvents: isDesignLocked || isUpdating ? "none" : "auto",
              "&:hover": {
                boxShadow: "0px 0px 0px 2px rgba(36, 132, 186, 0.10)",
              },
            }}
          >
            <Typography variant="actionSmallIcon">
              {t("specs.addDecoration")}
            </Typography>
            {!isDesignLocked && <Icon icon="add" size={24} />}
          </Box>
        </FlexColBox>
      )}
      {isMobile && (
        <LeftSidebarItem
          isDesignLocked={isDesignLocked || isUpdating}
          item={{
            label: t("specs.decorations"),
            display: {
              menuIconUrl: "",
              displayable: true,
              displayMethod: "icon",
              icon: "add" as MaterialSymbolType,
            },
            onClick: isDesignLocked || isUpdating ? undefined : addDecoration,
            isSelected: false,
            isDefault: true,
            canDelete: false,
            canHide: false,
            active: false,
            enabled: true,
          }}
          isDecoration={true}
        />
      )}
      {options?.map(
        (o) =>
          o.display.displayable && (
            <LeftSidebarItem
              isDesignLocked={isDesignLocked}
              item={o}
              key={o.id}
              isDecoration={true}
            />
          )
      )}
    </FlexColBox>
  );
};

export default Decorations;
